import React, { useState, useEffect, useCallback } from "react";
import { Select, Input, message, Spin, Button, Tooltip } from "antd";
import { http } from "../../../../services/http";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const today = new Date();
const day = ("0" + today.getDate()).slice(-2);
const month = ("0" + (today.getMonth() + 1)).slice(-2);
const year = today.getFullYear();
const formattedDate = `${year}-${month}-${day}`;

const fiveDaysAgo = new Date();
fiveDaysAgo.setDate(today.getDate() - 5);
const dayFiveAgo = ("0" + fiveDaysAgo.getDate()).slice(-2);
const monthFiveAgo = ("0" + (fiveDaysAgo.getMonth() + 1)).slice(-2);
const yearFiveAgo = fiveDaysAgo.getFullYear();
const minDate = `${yearFiveAgo}-${monthFiveAgo}-${dayFiveAgo}`;

// Utility function to format numbers with spaces
const formatNumberWithSpaces = (number) => {
  if (!number || isNaN(number)) return "0";
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

// Utility function to fix floating point arithmetic issues
const fixFloatingPoint = (num) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

function CreateProduct({ onClose, visible }) {
  let salesPersonCode = useSelector((state) => state.main.salesPersonCode);
  const employeeId = useSelector((state) => state.main.employeeId);

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState({
    fdata: false,
    pdata: false,
    wdata: false,
  });

  const [data, setData] = useState({
    fdata: [],
    pdata: [],
    wdata: [],
    cardCode: "",
    warehouseCode: "",
    listNum: null,
  });

  const [items, setItems] = useState([
    {
      itemCode: "",
      quantity: "",
      unitPrice: "",
      currency: "",
      selectedPrice: null,
    },
  ]);

  const [date, setDate] = useState(formattedDate);

  const { fdata, pdata, cardCode } = data;

  const resetForm = useCallback(() => {
    // Mahsulotlar ro'yxatini tozalash
    setItems([
      {
        itemCode: "",
        quantity: "",
        unitPrice: "",
        currency: "",
        selectedPrice: null,
      },
    ]);

    // Barcha ma'lumotlarni tozalash
    setData((prevState) => ({
      ...prevState,
      cardCode: "",
      warehouseCode: "",
      listNum: null,
    }));

    // Sana tozalash
    setDate(formattedDate);

    // Loading holatlarini tozalash
    setLoading(false);
    setLoadingData({
      fdata: false,
      pdata: false,
      wdata: false,
    });
  }, []);

  // Modal yopish funksiyasini yangilash
  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoadingData({ fdata: true, pdata: true, wdata: true });
      await Promise.all([fetchData(), fetchWarehouse(), fetchPartner()]);
      setLoadingData({ fdata: false, pdata: false, wdata: false });
    };

    if (visible) {
      // Modal ochilganda barcha ma'lumotlarni tozalash
      resetForm();
      fetchAllData();
    }
  }, [visible, resetForm]);

  // Mijoz tanlanganda maxsulot narxlarini yangilash
  useEffect(() => {
    if (cardCode && fdata.length > 0) {
      const selectedClient = pdata.find((item) => item.code === cardCode);
      if (selectedClient) {
        updateProductPrices(selectedClient.listNum);
      }
    }
  }, [cardCode, fdata.length, pdata]);

  // Modal yopilganda ma'lumotlarni tozalash
  useEffect(() => {
    if (!visible) {
      // Modal yopilganda barcha ma'lumotlarni to'liq tozalash
      resetForm();
    }
  }, [visible, resetForm]);

  // Modal yopilganda ESC tugmasi yoki tashqi tomondan yopilganda tozalash
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && visible) {
        handleClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [visible, handleClose]);

  const fetchData = async () => {
    try {
      const { data } = await http.get(`api/items`);
      const mappedItems = data.map((item) => ({
        code: item.itemCode,
        name: item.itemName,
        price: item.itemPriceUzs
          ? item.itemPriceUzs.price
          : item.itemPrice.price,
        currency: item.itemPriceUzs
          ? item.itemPriceUzs.currency !== null
            ? item.itemPriceUzs.currency
            : "UZS"
          : item.itemPrice.currency !== null
            ? item.itemPrice.currency
            : "USD",
        priceList: item.itemPriceUzs
          ? item.itemPriceUzs.priceList
          : item.itemPrice.priceList,
        // Store all price types for dynamic switching based on listNum
        itemPrice: item.itemPrice
          ? {
              price: item.itemPrice.price,
              currency: item.itemPrice.currency || "USD",
              priceList: item.itemPrice.priceList,
            }
          : null,
        itemPriceUzs: item.itemPriceUzs
          ? {
              price: item.itemPriceUzs.price,
              currency: item.itemPriceUzs.currency || "UZS",
              priceList: item.itemPriceUzs.priceList,
            }
          : null,
        itemPrice7: item.itemPrice7
          ? {
              price: item.itemPrice7.price,
              currency: item.itemPrice7.currency || "",
              priceList: item.itemPrice7.priceList,
            }
          : null,
      }));
      setData((prevState) => ({
        ...prevState,
        fdata: mappedItems,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchWarehouse = async () => {
    try {
      const { data } = await http.get(`api/warehouses`);
      const mappedItems = data.map((item) => ({
        code: item.warehouseCode,
        name: item.warehouseName,
      }));
      setData((prevState) => ({
        ...prevState,
        wdata: mappedItems,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPartner = async () => {
    try {
      const { data } = await http.get(`api/businesspartners/mijozlar`);
      const mappedItems = data.map((item) => ({
        code: item.cardCode,
        name: item.cardName,
        currency: item.currency,
        listNum: item.listNum,
      }));
      setData((prevState) => ({
        ...prevState,
        pdata: mappedItems,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const handleSelectChange = (index, value) => {
    const selectedItem = fdata.find((item) => item.name === value);
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      selectedPrice: selectedItem.price,
      itemCode: selectedItem.code,
      unitPrice: selectedItem.price,
      currency: selectedItem.currency,
    };
    setItems(newItems);
  };

  const handleMijozChange = (value) => {
    const selectedItem = pdata.find((item) => item.name === value);
    setData((prevState) => ({
      ...prevState,
      cardCode: selectedItem.code,
      listNum: selectedItem.listNum,
    }));

    // Clear product data when client changes
    clearProductData();

    // Update product prices based on client listNum
    updateProductPrices(selectedItem.listNum);
  };

  const updateProductPrices = (listNum) => {
    setData((prevState) => {
      const updatedFdata = prevState.fdata.map((item) => {
        let selectedPrice = null;
        let selectedCurrency = "";

        if (listNum === 11) {
          // listNum 11 bo'lsa itemPrice dan olish
          selectedPrice = item.itemPrice?.price || 0;
          selectedCurrency = item.itemPrice?.currency || "USD";
        } else if (listNum === 14) {
          // listNum 14 bo'lsa itemPriceUzs dan olish
          selectedPrice = item.itemPriceUzs?.price || 0;
          selectedCurrency = item.itemPriceUzs?.currency || "UZS";
        } else if (listNum === 7) {
          // listNum 7 bo'lsa itemPrice7 dan olish
          selectedPrice = item.itemPrice7?.price || 0;
          selectedCurrency = item.itemPrice7?.currency || "";
        } else {
          // Default holatda itemPrice dan olish
          selectedPrice =
            item.itemPrice?.price || item.itemPriceUzs?.price || 0;
          selectedCurrency =
            item.itemPrice?.currency || item.itemPriceUzs?.currency || "USD";
        }

        return {
          ...item,
          price: selectedPrice,
          currency: selectedCurrency,
        };
      });

      return {
        ...prevState,
        fdata: updatedFdata,
      };
    });
  };

  // const handleOmborChange = (value) => {
  //   const selectedItem = wdata.find((item) => item.name === value);
  //   setData((prevState) => ({
  //     ...prevState,
  //     warehouseCode: selectedItem.code,
  //   }));
  // };

  const handleQuantityChange = (index, value) => {
    // Remove spaces and non-numeric characters except decimal point
    const cleanValue = value.replace(/[^\d.]/g, "");
    // Don't allow "0" as initial value, keep it empty
    const finalValue = cleanValue === "0" ? "" : cleanValue;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], quantity: finalValue };
    setItems(newItems);
  };

  const removeItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        itemCode: "",
        quantity: "",
        unitPrice: "",
        currency: "",
        selectedPrice: null,
      },
    ]);
  };

  const postData = async () => {
    const allItemsValid = items.every(
      (item) => item.itemCode && item.quantity > 0,
    );
    if (cardCode && employeeId && allItemsValid) {
      setLoading(true);
      try {
        const documentLines = items.map((item, index) => ({
          itemCode: item.itemCode,
          quantity: item.quantity,
          lineNum: index,
          // warehouseCode: warehouseCode,
          currency: item.currency !== null ? item.currency : "USD",
          unitPrice: item.unitPrice,
        }));

        await http.post("api/yangisotuvyaratish", {
          cardCode,
          salesPersonCode,
          documentsOwner: employeeId,
          docDueDate: date,
          docDate: date,
          documentLines,
        });
        message.success(t("sucCreated"));

        setTimeout(() => {
          handleClose();
        }, 800);
      } catch (error) {
        message.error(t("errCreation") + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      message.error(t("fillForm"));
    }
  };

  const clearProductData = () => {
    setItems([
      {
        itemCode: "",
        quantity: "",
        unitPrice: "",
        currency: "",
        selectedPrice: null,
      },
    ]);
  };

  return (
    <div className="w-full px-4 sm:px-14">
      <div className="w-full">
        <div className="mt-10 flex flex-col items-center justify-between gap-4 sm:flex-row sm:gap-0">
          <div className="flex w-full flex-col gap-3 sm:w-auto">
            <p className="font-nunito">{t("mijoz")}</p>
            <Spin spinning={loadingData.pdata}>
              <Select
                value={
                  cardCode
                    ? pdata.find((item) => item.code === cardCode)?.name
                    : undefined
                }
                placeholder={t("choose")}
                showSearch
                optionFilterProp="children"
                onChange={handleMijozChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                className="flex h-12 w-full items-center justify-center sm:w-[300px]"
              >
                {pdata
                  .filter((item) => item.name !== null)
                  .map((item) => (
                    <Select.Option key={item.code} value={item.name}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Spin>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto">
            <p className="font-nunito">{t("date")}</p>
            <Input
              type="date"
              value={date}
              className="w-[150px]"
              onChange={(e) => {
                setDate(e.target.value);
              }}
              max={formattedDate}
              min={minDate}
            />
          </div>
        </div>

        <div className="mt-10 flex flex-col items-end gap-5 sm:flex-row">
          <div className="flex w-full flex-col sm:w-auto">
            {items.map((item, index) => (
              <div
                key={index}
                className="mt-1 flex flex-col items-start gap-5 sm:flex-row sm:items-center"
              >
                <div className="flex w-full flex-col gap-3 sm:w-auto">
                  {index === 0 ? <p>{t("item")}</p> : ""}
                  <Spin spinning={loadingData.fdata}>
                    {cardCode ? (
                      <Select
                        value={
                          item.itemCode
                            ? fdata.find(
                                (fItem) => fItem.code === item.itemCode,
                              )?.name
                            : undefined
                        }
                        placeholder={t("choose")}
                        onChange={(value) => handleSelectChange(index, value)}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="flex h-12 w-full items-center justify-center sm:w-[430px]"
                      >
                        {fdata
                          .filter((fItem) => fItem.name !== null)
                          .map((fItem) => (
                            <Select.Option key={fItem.code} value={fItem.name}>
                              {fItem.name}
                            </Select.Option>
                          ))}
                      </Select>
                    ) : (
                      <Tooltip title={"Avval mijozni tanlang"}>
                        <Select
                          disabled
                          placeholder={t("choose")}
                          className="flex h-12 w-full items-center justify-center sm:w-[430px]"
                        />
                      </Tooltip>
                    )}
                  </Spin>
                </div>

                <div className="flex w-full flex-col gap-3 sm:w-[150px]">
                  {index === 0 ? <p>{t("price")}</p> : ""}
                  <Input
                    value={`${formatNumberWithSpaces(item.selectedPrice)} ${item.currency || ""}`.trim()}
                    style={{ color: "black" }}
                    className="flex h-12 w-full items-center justify-center border-[1px] border-[#D0D0D0] sm:w-[150px]"
                    disabled
                  />
                </div>

                <div className="flex w-full flex-col gap-3 sm:w-[120px]">
                  {index === 0 ? <p>{t("quantity")}</p> : ""}
                  <Input
                    value={
                      item.quantity ? formatNumberWithSpaces(item.quantity) : ""
                    }
                    onChange={(e) =>
                      handleQuantityChange(index, e.target.value)
                    }
                    type="text"
                    placeholder={t("quantity")}
                    className="flex h-12 w-full items-center justify-center border-[1px] border-[#D0D0D0] sm:w-[120px]"
                  />
                </div>

                <div className="flex w-full flex-col gap-3 sm:w-[120px]">
                  {index === 0 ? <p>{t("priceSum")}</p> : ""}
                  <Input
                    value={`${formatNumberWithSpaces(
                      fixFloatingPoint(
                        Number(item.quantity) * Number(item.selectedPrice),
                      ),
                    )} ${item.currency || ""}`.trim()}
                    style={{ color: "black" }}
                    disabled
                    className="flex h-12 w-full items-center justify-center border-[1px] border-[#D0D0D0] sm:w-[120px]"
                  />
                </div>
                {items.length > 1 && (
                  <button
                    onClick={() => removeItem(index)}
                    className={`border-1 h-7 w-[30px] border-red-500 bg-red-500 text-white ${index === 0 ? "sm:mt-7" : "sm:mt-1"}`}
                  >
                    -
                  </button>
                )}
              </div>
            ))}
          </div>

          <div>
            <button
              onClick={addItem}
              className="mb-[7px] h-7 w-[30px] border-2 border-black bg-[#0A4D68] text-white"
            >
              +
            </button>
          </div>
        </div>

        {/* <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
          <div className="flex w-full flex-col gap-3 sm:w-auto">
            <p>{t("whs")}</p>
            <Spin spinning={loadingData.wdata}>
              <Select
                value={
                  warehouseCode
                    ? wdata.find((item) => item.code === warehouseCode)?.name
                    : undefined
                }
                placeholder={t("choose")}
                showSearch
                optionFilterProp="children"
                onChange={handleOmborChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                className="flex h-12 w-full items-center justify-center sm:w-[250px]"
              >
                {wdata
                  .filter((item) => item.name !== null)
                  .map((item) => (
                    <Select.Option key={item.code} value={item.name}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Spin>
          </div>
        </div> */}

        <div className="mt-24 flex justify-center">
          <Button
            onClick={postData}
            className="h-12 w-full rounded-3xl bg-[#0A4D68] text-white sm:w-[350px]"
            disabled={loading}
            loading={loading}
          >
            {loading ? t("saving") : t("saveAndCreate")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CreateProduct;
